import { type DeepRequired, type FieldErrorsImpl } from 'react-hook-form';
import * as yup from 'yup';

/**
 * Converts a flat path like "assignments[0].user" to a nested object structure
 * that react-hook-form expects for error handling.
 */
const setNestedError = (obj: Record<string, unknown>, path: string, error: { type: string; message: string }): void => {
  // Convert bracket notation to dot notation: "assignments[0].user" -> "assignments.0.user"
  // eslint-disable-next-line i18next/no-literal-string
  const normalizedPath = path.replace(/\[(\d+)\]/g, '.$1');
  const parts = normalizedPath.split('.');

  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    const nextPart = parts[i + 1];
    const isNextPartArrayIndex = /^\d+$/.test(nextPart);

    if (!(part in current)) {
      current[part] = isNextPartArrayIndex ? [] : {};
    }
    current = current[part] as Record<string, unknown>;
  }

  const lastPart = parts[parts.length - 1];
  current[lastPart] = error;
};

export const yupValidationResolver =
  <T>(validationSchema?: yup.Schema<T>) =>
  async (data: T) => {
    if (!validationSchema) {
      return {
        values: data,
        errors: {}
      };
    }
    try {
      const values = await validationSchema.validate(data, {
        abortEarly: false
      });

      return {
        values,
        errors: {}
      };
    } catch (errors) {
      const errorList = (errors as { inner: { path: string; type: string; message: string }[] })?.inner;

      // Build a properly nested error object
      const nestedErrors: Record<string, unknown> = {};
      errorList?.forEach(error => {
        if (error.path) {
          setNestedError(nestedErrors, error.path, { type: error.type, message: error.message });
        }
      });

      return {
        values: {},
        errors: nestedErrors as Partial<FieldErrorsImpl<DeepRequired<T>>>
      };
    }
  };
